/* eslint-disable camelcase */
import { cn, Divider, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { PlotReportFactLabelNameEnum, R1FactType, SpeciesEnum } from '@/api/rest/resources/types/fact';
import { PlotOverview } from '@/api/rest/resources/types/plot';
import { UnitEnum } from '@/api/rest/resources/types/units';
import { PlotTypeIcon } from '@/components';
import { DataGroup, DataStack, DataStackContent, DataStackLabel } from '@/components/DataLabel/DataLabel';
import { MultilineRenderer } from '@/components/MultilineRenderer';
import { EcosystemIntegrityIndexPill } from '@/components/Pill/EcosystemIntegrityIndexPill';
import { AccordionSection, AccordionType, AccordionWrapper } from '@/components/widgets/o/Accordion/Accordion';
import { NO_DATA_PLACEHOLDER } from '@/config/constants';
import { useDisplayNumber } from '@/hooks/useDisplayNumber';
import { usePlotLocation } from '@/hooks/usePlotLocation';
import { usePlotOverview } from '@/hooks/usePlotOverview';
import { usePlot } from '@/pages/shared/hooks/usePlot';
import { usePlotFacts } from '@/pages/shared/hooks/usePlotFact';
import { getFactFromFactArray } from '@/pages/shared/hooks/utils';
import { PlotOverviewContainer } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/PlotOverview';
import { MarinePill } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plot/components/speciesGradient/OverviewStatsTile';
import { isNullish } from '@/utils/booleans';
import { formatUnit } from '@/utils/formatting';
import { squareMetersToHectares } from '@/utils/plot';

import { MediaFiles } from './components/MediaFile';

export const PlotOverviewPage = () => {
  return (
    <Stack spacing={10} direction='col' data-testid='plot-overview-page'>
      <PlotOverviewInfo />
      <OverviewAccordionSections />
    </Stack>
  );
};

const PlotOverviewInfo = () => {
  const { t } = useTranslation();

  const plot = usePlot().data;

  const location = usePlotLocation({ plotId: plot.id });
  const areaDisplay = `${useDisplayNumber(squareMetersToHectares(plot.area))} ${formatUnit(UnitEnum.ha)}`;

  const biodiversityUpliftFacts = usePlotFacts<number>([R1FactType.marine_biodiversity_uplift]);
  const biodiversityUpliftFact = getFactFromFactArray(biodiversityUpliftFacts, R1FactType.marine_biodiversity_uplift, {
    [PlotReportFactLabelNameEnum.species]: SpeciesEnum.posidonia,
  });

  const displayBiodiveristyUplift = typeof biodiversityUpliftFact?.value === 'number';

  return (
    <PlotOverviewContainer>
      <DataGroup
        className={`grid grid-cols-1 gap-6 ${displayBiodiveristyUplift ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}
      >
        <DataStack>
          <DataStackLabel>{t('global.plot.mapPopup.labels.plotName')}</DataStackLabel>
          <DataStackContent className=''>{plot.name ?? NO_DATA_PLACEHOLDER}</DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.eii')}</DataStackLabel>
          <DataStackContent>
            <EcosystemIntegrityIndexPill />
          </DataStackContent>
        </DataStack>
        {displayBiodiveristyUplift && (
          <DataStack>
            <DataStackLabel>{t('shared.ncaDetail.details.speciesGradient.labels.biodiversityUplift')}</DataStackLabel>
            <DataStackContent>
              <MarinePill value={biodiversityUpliftFact.value as number} unit={biodiversityUpliftFact.unit} />
            </DataStackContent>
          </DataStack>
        )}
      </DataGroup>

      <Divider />

      <DataGroup className={cn('grid grid-cols-1 gap-6 md:grid-cols-2', displayBiodiveristyUplift && 'md:grid-cols-3')}>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.plotSize')}</DataStackLabel>
          <DataStackContent>{areaDisplay ?? NO_DATA_PLACEHOLDER}</DataStackContent>
        </DataStack>
        {plot.type && (
          <DataStack>
            <DataStackLabel>{t('global.plot.plotLandType')}</DataStackLabel>
            <DataStackContent>
              <Stack direction='row' spacing={1}>
                <PlotTypeIcon type={plot.type} size={20} />
                <span>{t(`global.plotTypes.${plot.type}`)}</span>
              </Stack>
            </DataStackContent>
          </DataStack>
        )}
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.location')}</DataStackLabel>
          <DataStackContent>{location ?? NO_DATA_PLACEHOLDER}</DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.biome')}</DataStackLabel>
          <DataStackContent>{plot?.biome ?? NO_DATA_PLACEHOLDER}</DataStackContent>
        </DataStack>
        <DataStack>
          <DataStackLabel>{t('shared.ncaDetail.details.labels.ecoregions')}</DataStackLabel>
          <DataStackContent>{plot?.ecoregions ?? NO_DATA_PLACEHOLDER}</DataStackContent>
        </DataStack>
      </DataGroup>
    </PlotOverviewContainer>
  );
};

const OverviewAccordionSections = () => {
  const sections = usePlotOverview().data;

  if (sections.length === 0) {
    return null;
  }

  return (
    <>
      <AccordionWrapper type={AccordionType.MULTIPLE} defaultValue={[]}>
        {sections.map((section, index) => (
          <div key={`overview-section-${index}`}>
            <OverviewSection {...section} />
          </div>
        ))}
      </AccordionWrapper>
    </>
  );
};

const OverviewSection = ({ title, description, media_files }: PlotOverview) => {
  const { t } = useTranslation();

  return (
    <AccordionSection
      title={title}
      value={title.replaceAll(' ', '-').toLowerCase()}
      styles={{
        trigger: 'bg-neutral-black-4',
        content: 'border border-divider rounded-b-3xl',
      }}
    >
      {isNullish(description) ? (
        <span className='typography-body1'>{t('shared.projects.plot.tabView.overview.labels.noContent')}</span>
      ) : (
        <MultilineRenderer text={description} styles={{ paragraph: 'py-1' }} />
      )}
      {media_files?.length > 0 && <MediaFiles mediaFiles={media_files} />}
    </AccordionSection>
  );
};
